/**
 * Notification system types and interfaces
 */

// Notification channels
export type NotificationChannel = 'email' | 'sms' | 'push';

// Notification event types (from new NotificationService system)
// These match the 15 event types defined in the platform backend
export type NotificationEventType = 
  // Token events
  | 'tokenCreated'            // TOKEN_CREATED - When you create a cash token (replaces legacy GENERATED_CODE)
  | 'tokenReceived'           // TOKEN_RECEIVED - When payee receives token
  | 'tokenAutoDeposited'      // TOKEN_AUTO_DEPOSITED - When token auto-deposited to balance
  | 'tokenAutoRevealed'       // TOKEN_AUTO_REVEALED - When token auto-revealed to inbox
  | 'tokenExpiring'           // TOKEN_EXPIRING - When token is about to expire
  | 'tokenRedeemed'           // TOKEN_REDEEMED - When payer's code is redeemed
  // Invoice events
  | 'invoicePaid'             // INVOICE_PAID - When invoice has been paid
  | 'invoiceAutoPaid'         // INVOICE_AUTO_PAID - When invoice auto-paid from balance
  | 'invoiceReceived'         // INVOICE_RECEIVED - When user receives new invoice
  // Transfer events
  | 'transferCompleted'       // TRANSFER_COMPLETED - When transfer successfully completed
  | 'transferFailed'          // TRANSFER_FAILED - When transfer fails
  // Account events
  | 'lowBalance'              // LOW_BALANCE - When balance falls below threshold
  | 'loginNewDevice'          // LOGIN_NEW_DEVICE - When user logs in from new device
  | 'passwordChanged'        // PASSWORD_CHANGED - When user changes password
  // Merchant events (merchant only)
  | 'merchantPaymentReceived' // MERCHANT_PAYMENT_RECEIVED - When merchant receives payment
  | 'merchantRefundIssued';  // MERCHANT_REFUND_ISSUED - When merchant issues refund

// Template variable types
export interface TemplateVariable {
  name: string;
  description: string;
  example: string;
  required?: boolean;
}

// Notification template interface
export interface NotificationTemplate {
  _id?: string;
  eventType: NotificationEventType;
  channel: NotificationChannel;
  name: string;
  description?: string;
  active: boolean;
  
  // Email-specific fields
  subject?: string;
  body: string;
  html?: string;
  
  // SMS-specific fields (body is reused)
  
  // Push notification fields
  title?: string;
  // body is reused for push
  
  // Template variables
  variables?: string[];
  
  // App-specific targeting
  appId?: string;
  
  // Localization
  locale?: string;
  
  // Version control
  version?: number;
  isDefault?: boolean;
  
  // Testing
  testData?: Record<string, any>;
  
  // Analytics
  usageCount?: number;
  lastUsedAt?: Date;
  
  // Metadata
  createdBy?: string;
  createdAt?: Date;
  updatedBy?: string;
  updatedAt?: Date;
}

// User notification preferences
export interface NotificationPreferences {
  // Global settings
  enabled: boolean;
  
  // Channel-specific global settings
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  
  // Per-event preferences (simple on/off per channel)
  events: {
    [key in NotificationEventType]?: {
      email: boolean;
      sms: boolean;
      push: boolean;
    };
  };
  
  // Quiet hours (optional, for future)
  quietHours?: {
    enabled: boolean;
    startTime: string; // "22:00"
    endTime: string;   // "08:00"
    timezone: string;  // "America/New_York"
  };
  
  // Frequency limits (optional, for future)
  frequencyLimits?: {
    maxPerHour?: number;
    maxPerDay?: number;
  };
}

// Template test request
export interface TemplateTestRequest {
  recipientUserId?: string;
  testData: Record<string, any>;
}

// Template test response
export interface TemplateTestResponse {
  success: boolean;
  preview?: {
    subject?: string;
    body: string;
    html?: string;
  };
  error?: string;
}

// Notification event metadata for UI
export interface NotificationEventMetadata {
  type: NotificationEventType;
  label: string;
  description: string;
  category: 'tokens' | 'invoices' | 'transfers' | 'account' | 'merchant';
  defaultChannels: NotificationChannel[];
  priority?: 'low' | 'medium' | 'high';
  merchantOnly?: boolean; // If true, only shown for merchants
}

// Event metadata definitions - matches new NotificationService system
export const NOTIFICATION_EVENTS: NotificationEventMetadata[] = [
  // Token events
  {
    type: 'tokenCreated',
    label: 'Token Created',
    description: 'When you create a cash token from your account',
    category: 'tokens',
    defaultChannels: ['email'],
    priority: 'low'
  },
  {
    type: 'tokenReceived',
    label: 'Token Received',
    description: 'When you receive a token from someone (payee receives token)',
    category: 'tokens',
    defaultChannels: ['email'],
    priority: 'medium'
  },
  {
    type: 'tokenAutoDeposited',
    label: 'Token Auto-Deposited',
    description: 'When a token is automatically deposited to your balance',
    category: 'tokens',
    defaultChannels: ['email'],
    priority: 'high'
  },
  {
    type: 'tokenAutoRevealed',
    label: 'Token Auto-Revealed',
    description: 'When a token is auto-revealed to inbox (not deposited)',
    category: 'tokens',
    defaultChannels: ['email'],
    priority: 'medium'
  },
  {
    type: 'tokenExpiring',
    label: 'Token Expiring',
    description: 'When a token you sent is about to expire',
    category: 'tokens',
    defaultChannels: ['email'],
    priority: 'low'
  },
  {
    type: 'tokenRedeemed',
    label: 'Token Redeemed',
    description: 'When your code is redeemed by someone else (payer notification)',
    category: 'tokens',
    defaultChannels: ['email'],
    priority: 'medium'
  },
  // Invoice events
  {
    type: 'invoicePaid',
    label: 'Invoice Paid',
    description: 'When an invoice has been paid',
    category: 'invoices',
    defaultChannels: ['email'],
    priority: 'medium'
  },
  {
    type: 'invoiceAutoPaid',
    label: 'Invoice Auto-Paid',
    description: 'When an invoice is automatically paid from your balance',
    category: 'invoices',
    defaultChannels: ['email'],
    priority: 'high'
  },
  {
    type: 'invoiceReceived',
    label: 'Invoice Received',
    description: 'When you receive a new invoice to pay',
    category: 'invoices',
    defaultChannels: ['email'],
    priority: 'low'
  },
  // Transfer events
  {
    type: 'transferCompleted',
    label: 'Transfer Completed',
    description: 'When a transfer is successfully completed',
    category: 'transfers',
    defaultChannels: ['email'],
    priority: 'medium'
  },
  {
    type: 'transferFailed',
    label: 'Transfer Failed',
    description: 'When a transfer fails',
    category: 'transfers',
    defaultChannels: ['email'],
    priority: 'high'
  },
  // Account events
  {
    type: 'lowBalance',
    label: 'Low Balance',
    description: 'When your balance falls below threshold',
    category: 'account',
    defaultChannels: ['email'],
    priority: 'low'
  },
  {
    type: 'loginNewDevice',
    label: 'Login from New Device',
    description: 'When you log in from a new device',
    category: 'account',
    defaultChannels: ['email', 'sms'],
    priority: 'high'
  },
  {
    type: 'passwordChanged',
    label: 'Password Changed',
    description: 'When you change your password',
    category: 'account',
    defaultChannels: ['email', 'sms'],
    priority: 'high'
  },
  // Merchant events (merchant only)
  {
    type: 'merchantPaymentReceived',
    label: 'Payment Received',
    description: 'When you receive a payment as a merchant',
    category: 'merchant',
    defaultChannels: ['push'],
    priority: 'medium',
    merchantOnly: true
  },
  {
    type: 'merchantRefundIssued',
    label: 'Refund Issued',
    description: 'When you issue a refund as a merchant',
    category: 'merchant',
    defaultChannels: ['email', 'push'],
    priority: 'high',
    merchantOnly: true
  }
];

// Helper to group events by category
export const NOTIFICATION_EVENT_CATEGORIES = {
  tokens: 'Token Events',
  invoices: 'Invoice Events',
  transfers: 'Transfer Events',
  account: 'Account Events',
  merchant: 'Merchant Events'
} as const;
